import React from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { PublicHeader, PublicFooter } from "@/components/layout/Layout";

const META = {
  mentions: {
    title: "Mentions légales — VosCompagnons",
    description: "Mentions légales de VosCompagnons, plateforme B2B de mise en relation dans le bâtiment : éditeur, hébergeur, propriété intellectuelle, contact.",
    path: "/mentions-legales",
  },
  rgpd: {
    title: "Politique de confidentialité (RGPD) — VosCompagnons",
    description: "Comment VosCompagnons traite les données à caractère personnel : finalités, bases légales, destinataires, durées de conservation, droits des personnes.",
    path: "/confidentialite",
  },
  responsabilite: {
    title: "Responsabilité RGPD — VosCompagnons",
    description: "Qualification RGPD des intervenants sur VosCompagnons et répartition des responsabilités entre la plateforme et les utilisateurs professionnels.",
    path: "/responsabilite-rgpd",
  },
  cgu: {
    title: "Conditions générales d'utilisation — VosCompagnons",
    description: "CGU de VosCompagnons : éligibilité professionnelle, obligations de conformité documentaire, rôle de la plateforme comme tiers de confiance de mise en relation.",
    path: "/cgu",
  },
  cgv: {
    title: "Conditions générales de vente — VosCompagnons",
    description: "CGV de VosCompagnons : inscription et usage gratuits pour les artisans, commission de 8 % uniquement sur les chantiers réalisés via la plateforme, abonnement entreprises, facturation, résiliation.",
    path: "/cgv",
  },
  cookies: {
    title: "Politique cookies — VosCompagnons",
    description: "Politique cookies de VosCompagnons : cookies techniques strictement nécessaires, aucun tracker publicitaire.",
    path: "/cookies",
  },
};

const CONTENTS = {
  mentions: {
    title: "Mentions légales",
    sections: [
      { h: "1. Éditeur du site", p: [
        "VosCompagnons — Une relation de savoir-faire.",
        "Forme juridique : à préciser lors de l'immatriculation (SAS / SARL).",
        "Siège social : à compléter avant mise en production.",
        "Capital social : à compléter.",
        "SIREN : à compléter.",
        "N° TVA intracommunautaire : à compléter.",
        "Directeur de la publication : Louis-Philippe (représentant légal — contact@voscompagnons.fr).",
      ]},
      { h: "2. Contact", p: [
        "Adresse e-mail : contact@voscompagnons.fr.",
        "Toute réclamation peut être adressée à cette adresse. Un accusé de réception sera envoyé sous 5 jours ouvrés.",
      ]},
      { h: "3. Hébergement", p: [
        "L'application est hébergée sur une infrastructure cloud opérée depuis l'Union européenne.",
        "Hébergeur : Emergent (voir mentions techniques mises à jour lors du passage en production).",
      ]},
      { h: "4. Propriété intellectuelle", p: [
        "L'ensemble des éléments de la plateforme (marque VosCompagnons, logo, code, interfaces, contenus éditoriaux) sont protégés par le droit d'auteur et le droit des marques.",
        "Toute reproduction, diffusion ou exploitation non autorisée est interdite.",
      ]},
      { h: "5. Public visé", p: [
        "La plateforme est strictement réservée aux professionnels du bâtiment (entreprises, artisans, micro-entrepreneurs, syndics, gestionnaires de patrimoine) disposant d'un numéro SIRET valide.",
        "L'inscription de particuliers est refusée par les CGU.",
      ]},
      { h: "6. Loi applicable", p: [
        "Les présentes mentions sont régies par le droit français.",
        "En cas de litige, compétence exclusive du tribunal du siège de l'éditeur, sauf disposition d'ordre public contraire.",
      ]},
    ],
  },

  rgpd: {
    title: "Politique de confidentialité (RGPD)",
    sections: [
      { h: "1. Responsable de traitement", p: [
        "VosCompagnons est responsable de traitement au sens de l'article 4-7 du Règlement (UE) 2016/679 (« RGPD »).",
        "Contact : contact@voscompagnons.fr. Un délégué à la protection des données (DPO) sera désigné et publié ici dès mise en production.",
      ]},
      { h: "2. Données collectées", p: [
        "Données d'identification professionnelle : e-mail, mot de passe (haché en bcrypt), rôle (entreprise / prestataire), raison sociale, prénom et nom (prestataires), SIRET, téléphone.",
        "Données professionnelles : corps de métier, spécialités, tarifs, portfolio, certifications, disponibilités, zone d'intervention (latitude/longitude issues d'un géocodage d'adresse).",
        "Documents de conformité : KBIS, attestation URSSAF, RC Pro, décennale, attestation fiscale. Stockés de manière privée, accessibles uniquement par URL signée à durée limitée.",
        "Données transactionnelles : besoins publiés, candidatures, missions, montants convenus HT, statut, avis émis et reçus.",
        "Données techniques : adresses IP tronquées, journaux d'authentification (tentatives échouées), journaux d'accès aux blocs sensibles (accès chantier — à venir).",
        "Aucune donnée particulièrement sensible au sens de l'article 9 du RGPD n'est collectée.",
      ]},
      { h: "3. Finalités", p: [
        "Mise en relation B2B entre donneurs d'ordre et prestataires.",
        "Vérification de la conformité documentaire des prestataires (obligation légale de vigilance du donneur d'ordre — articles L.8222-1 et suivants du Code du travail).",
        "Gestion des candidatures, missions et avis.",
        "Facturation (à venir) et prévention de la fraude.",
        "Amélioration du service et mesure d'audience anonymisée.",
      ]},
      { h: "4. Bases légales", p: [
        "Exécution du contrat qui lie l'utilisateur à VosCompagnons (article 6-1-b du RGPD) pour la mise en relation, la gestion de compte, les missions.",
        "Obligation légale (article 6-1-c) pour la conservation des documents administratifs et la traçabilité des relations professionnelles entre entreprises et prestataires indépendants.",
        "Intérêt légitime (article 6-1-f) pour la sécurité de la plateforme et la prévention de la fraude, avec une balance en faveur des personnes concernées.",
      ]},
      { h: "5. Destinataires", p: [
        "En interne : équipe VosCompagnons habilitée (administration, support, DPO).",
        "Autres utilisateurs de la plateforme : uniquement dans les limites strictes prévues par les règles d'accès (par exemple, un donneur d'ordre ne voit les documents de conformité d'un prestataire que s'il consulte son profil ; un prestataire ne voit les documents contractuels et le bloc « Accès chantier » que s'il est le prestataire retenu et tant que la mission est active).",
        "Sous-traitants techniques (hébergeur, prestataire de géocodage api-adresse.data.gouv.fr — géocodage sans transmission de données personnelles). Aucun transfert publicitaire.",
        "Autorités : sur réquisition légale uniquement.",
      ]},
      { h: "6. Transferts hors UE", p: [
        "Aucun transfert de données à caractère personnel hors Union européenne n'est prévu en l'état.",
        "Si un tel transfert devait avoir lieu, il serait encadré par les clauses contractuelles types de la Commission européenne et notifié dans la présente politique.",
      ]},
      { h: "7. Durées de conservation", p: [
        "Compte utilisateur : 3 ans après la dernière connexion active. Notification préalable avant suppression.",
        "Documents de conformité (KBIS, attestations) : 10 ans après la fin de la relation contractuelle (obligation de traçabilité des relations professionnelles).",
        "Historique des missions et facturation : 10 ans (obligation comptable et fiscale).",
        "Bloc « Accès chantier » (codes, contact, photos d'accès) : purge automatique 30 jours après la date de fin de mission.",
        "Journaux techniques d'authentification : 1 an.",
        "Journaux de consultation du bloc accès chantier : conservés le temps de la mission + 3 ans à des fins probatoires.",
      ]},
      { h: "8. Sécurité", p: [
        "Mots de passe hachés (bcrypt) — jamais stockés en clair.",
        "Authentification par jetons JWT en cookies httpOnly + Secure + SameSite=none.",
        "Documents privés servis exclusivement via URL signées HMAC-SHA256 à durée limitée (5 minutes). Aucun accès direct au disque.",
        "Codes d'accès chantier chiffrés en base (Fernet — chiffrement symétrique authentifié) avec clé stockée en variable d'environnement, jamais en clair, jamais versionnée.",
        "Journalisation des consultations sensibles pour audit.",
      ]},
      { h: "9. Vos droits", p: [
        "Droit d'accès, de rectification, d'effacement, de limitation, d'opposition, de portabilité (articles 15 à 22 du RGPD).",
        "Vous pouvez exercer ces droits en écrivant à contact@voscompagnons.fr. Une réponse vous sera apportée sous un mois maximum.",
        "Vous disposez également du droit d'introduire une réclamation auprès de la CNIL — 3 place de Fontenoy, TSA 80715, 75334 PARIS CEDEX 07 — www.cnil.fr.",
      ]},
      { h: "10. Modifications", p: [
        "La présente politique peut être mise à jour. Toute modification substantielle sera notifiée aux utilisateurs par e-mail ou bandeau in-app avant application.",
      ]},
    ],
  },

  responsabilite: {
    title: "Responsabilité RGPD",
    sections: [
      { h: "1. Objet de cette page", p: [
        "Ce document précise, pour éviter toute ambiguïté juridique, la qualification RGPD de chaque intervenant sur la plateforme VosCompagnons et la répartition des responsabilités en matière de protection des données.",
      ]},
      { h: "2. VosCompagnons — Responsable de traitement pour ses propres traitements", p: [
        "VosCompagnons est responsable de traitement (au sens de l'article 4-7 du RGPD) pour les traitements qui lui sont propres : gestion des comptes, authentification, vérification de conformité documentaire, historique des missions, facturation à venir, sécurité et prévention de la fraude.",
        "À ce titre, VosCompagnons définit les finalités et les moyens de ces traitements et en assume la responsabilité vis-à-vis des utilisateurs et de la CNIL.",
      ]},
      { h: "3. Utilisateurs — Responsables de traitement pour les données qu'ils publient", p: [
        "Les entreprises donneuses d'ordre et les prestataires indépendants sont responsables de traitement pour les données à caractère personnel qu'ils publient sur la plateforme ou qu'ils communiquent à d'autres utilisateurs via la messagerie, les besoins, les candidatures ou le bloc « Accès chantier ».",
        "Chaque utilisateur s'engage à ne publier que les données strictement nécessaires à la relation commerciale, à en assurer l'exactitude et à en obtenir le consentement lorsqu'il s'agit de données concernant des tiers (par exemple : contact sur place indiqué dans le bloc accès chantier).",
      ]},
      { h: "4. VosCompagnons — Sous-traitant au sens du RGPD pour les échanges entre utilisateurs", p: [
        "Pour les données transmises d'un utilisateur à un autre via la plateforme (messages, pièces jointes contractuelles, informations d'accès chantier), VosCompagnons agit comme sous-traitant technique au sens de l'article 28 du RGPD, pour le compte du responsable de traitement (l'utilisateur émetteur).",
        "À ce titre, VosCompagnons :",
        "• ne traite ces données que sur instruction documentée du responsable de traitement (via les fonctionnalités de la plateforme) ;",
        "• garantit la confidentialité par le contrôle d'accès (rôles, HMAC, chiffrement Fernet pour l'accès chantier) ;",
        "• assiste les utilisateurs dans l'exercice de leurs obligations (droits des personnes, notification de violation) ;",
        "• supprime ou restitue les données au terme de la prestation, conformément aux durées de conservation.",
      ]},
      { h: "5. Obligations partagées", p: [
        "Notification de violation de données : si un incident de sécurité est détecté, VosCompagnons notifie la CNIL dans les 72 heures et informe les utilisateurs concernés sans délai. Les utilisateurs s'engagent réciproquement à signaler à VosCompagnons toute violation portée à leur connaissance concernant des données transitant par la plateforme.",
        "Registre des traitements : VosCompagnons tient un registre au sens de l'article 30. Il est disponible sur demande motivée à contact@voscompagnons.fr.",
        "Analyse d'impact (AIPD) : une AIPD a été / sera menée pour le traitement du bloc « Accès chantier » compte tenu de la sensibilité des données (codes d'accès, contacts privés).",
      ]},
      { h: "6. Limites de responsabilité", p: [
        "VosCompagnons ne peut être tenue responsable de la fiabilité des informations publiées par les utilisateurs, ni des conséquences d'un usage frauduleux qu'un utilisateur ferait des données auxquelles il a légitimement accès (documents contractuels d'une mission qu'il a remportée, informations d'accès chantier).",
        "Chaque utilisateur reconnaît qu'un usage détourné constitue un manquement grave à ses obligations, susceptible d'entraîner la suspension immédiate de son compte, sans préjudice de poursuites civiles et pénales.",
      ]},
      { h: "7. Contact et recours", p: [
        "Pour toute question relative à la présente répartition de responsabilité : contact@voscompagnons.fr.",
        "Autorité de contrôle : Commission nationale de l'informatique et des libertés (CNIL) — 3 place de Fontenoy, TSA 80715, 75334 PARIS CEDEX 07 — www.cnil.fr.",
      ]},
    ],
  },

  cgu: {
    title: "Conditions générales d'utilisation (CGU)",
    sections: [
      { h: "1. Objet", p: ["Les présentes CGU définissent les modalités d'accès et d'utilisation de la plateforme VosCompagnons entre l'éditeur et l'utilisateur professionnel."] },
      { h: "2. Éligibilité", p: [
        "L'inscription est réservée aux personnes morales et travailleurs indépendants disposant d'un numéro SIRET valide.",
        "Toute inscription de particulier est refusée. L'éditeur se réserve le droit de vérifier la véracité du SIRET par appel à l'API officielle de l'INSEE.",
      ]},
      { h: "3. Compte utilisateur", p: [
        "L'utilisateur est responsable de la confidentialité de ses identifiants et de toute activité effectuée depuis son compte.",
        "Il s'engage à maintenir à jour les documents de conformité (KBIS, attestations) et à répondre aux demandes de validation de l'administrateur.",
      ]},
      { h: "4. Contenu publié", p: [
        "Chaque utilisateur est seul responsable des informations qu'il publie (profil, portfolio, besoins, candidatures, avis, pièces jointes).",
        "Sont notamment interdits : diffamation, atteinte à la vie privée, contenus contrefaisants, appel à la concurrence déloyale, contournement des règles applicables aux relations entre entreprises et prestataires indépendants.",
      ]},
      { h: "5. Modération", p: [
        "L'éditeur peut, à tout moment et sans indemnité, suspendre ou supprimer un compte non conforme (documents administratifs manquants ou expirés, avis diffamatoires, comportement déloyal, tentative de contournement du paiement).",
        "L'utilisateur suspendu peut contester la décision par écrit à contact@voscompagnons.fr dans un délai de 30 jours.",
      ]},
      { h: "6. Rôle de la plateforme", p: [
        "VosCompagnons est un tiers de confiance de mise en relation.",
        "La plateforme n'est jamais partie aux contrats conclus entre professionnels. Elle ne garantit ni la bonne exécution des missions ni le paiement.",
        "Elle assure toutefois : la vérification documentaire, la sécurité des échanges, le calcul de la commission et la conservation des preuves d'échange.",
      ]},
      { h: "6 bis. Commission de plateforme", p: [
        "Objet : en contrepartie de la mise en relation, du suivi documentaire et de la traçabilité des validations de versements, VosCompagnons perçoit une commission à la charge exclusive du prestataire indépendant (artisan). Le donneur d'ordre ne supporte aucune commission : ses montants contractuels restent inchangés.",
        "Assiette : le montant hors taxes de chaque versement validé par le donneur d'ordre au titre d'une commande de travaux conclue via la plateforme (acompte, situations de travaux, solde, libération de retenue de garantie). Un montant déjà commissionné ne l'est jamais une seconde fois.",
        "Taux : 8 % du montant HT au lancement, selon la grille de paliers en vigueur publiée dans l'espace artisan. La grille applicable à une commande est celle en vigueur au jour de son acceptation ; une modification ultérieure ne s'applique pas aux chantiers déjà engagés.",
        "Moment du prélèvement : la commission est due dès la validation du versement par le donneur d'ordre et prélevée sur le versement libéré au prestataire. Tant que le service de paiement n'est pas activé, elle est constatée au statut « commission due, en attente de prélèvement » et ne donne lieu à aucun paiement séparé.",
        "TVA : la commission est soumise à la TVA au taux légal en vigueur (20 %). Une facture conforme (Factur-X) est émise par la plateforme au prestataire pour chaque commission, consultable dans son espace.",
      ]},
      { h: "7. Résiliation", p: [
        "L'utilisateur peut résilier son compte à tout moment. Les données seront conservées selon les durées prévues par la politique de confidentialité.",
      ]},
      { h: "8. Droit applicable", p: ["Droit français. Tribunal compétent : celui du siège de l'éditeur, sauf disposition d'ordre public contraire."] },
    ],
  },

  cgv: {
    title: "Conditions générales de vente (CGV)",
    sections: [
      { h: "1. Accès à la plateforme", p: [
        "Pour les prestataires indépendants (artisans, auto-entrepreneurs, micro-entrepreneurs) : inscription et usage gratuits — création du profil, publication des disponibilités, candidature aux besoins publiés, réception des mises en relation. Une commission de 8 % est due uniquement sur les chantiers réalisés via la plateforme (voir article « Commission de plateforme »).",
        "Toute entreprise (donneur d'ordre) nouvellement inscrite bénéficie d'un freemium de 2 mises en relation offertes sans engagement ni carte bancaire, afin de tester la plateforme.",
      ] },
      { h: "2. Abonnement (entreprises)", p: [
        "Au-delà du freemium, l'accès complet pour les entreprises est conditionné à la souscription d'un abonnement mensuel, facturé d'avance. Aucun abonnement n'est requis pour les prestataires indépendants.",
        "Offre de lancement : toute entreprise inscrite avant le lancement commercial bénéficie d'un accès complet gratuit jusqu'au 31 décembre 2026, sans engagement ni carte bancaire. Aucune facturation automatique n'est déclenchée à cette échéance ; la souscription doit être explicitement acceptée par l'entreprise.",
        "À l'issue de cette période, le tarif applicable est de 99 € HT / mois — tarif de lancement — dans une formule unique donnant accès à l'ensemble des fonctionnalités (recherche et filtres illimités, suivi de conformité documentaire, publication de besoins illimitée, messagerie directe, avis entre professionnels).",
        "Inscription et usage gratuits. Commission de 8 % uniquement sur les chantiers réalisés via la plateforme. Aucun abonnement n'est requis pour les prestataires indépendants.",
        "Résiliation à tout moment avec un préavis d'un mois calendaire.",
      ]},
      { h: "3. Facturation & TVA", p: [
        "Les factures d'abonnement sont émises mensuellement au format PDF conforme aux exigences légales.",
        "La TVA est calculée au taux légal en vigueur.",
        "Tout montant impayé porte intérêt de plein droit au taux légal majoré, sans qu'une mise en demeure préalable ne soit nécessaire.",
      ]},
      { h: "4. Rétractation", p: [
        "S'agissant d'un service B2B fourni à un professionnel dans le cadre de son activité, aucun droit de rétractation ne s'applique.",
      ]},
    ],
  },

  cookies: {
    title: "Politique cookies",
    sections: [
      { h: "1. Nature des cookies utilisés", p: [
        "VosCompagnons utilise exclusivement des cookies techniques strictement nécessaires au fonctionnement du service : jetons d'authentification httpOnly (access_token, refresh_token) et cookie de session anti-CSRF le cas échéant.",
        "Ces cookies ne sont pas soumis au consentement préalable (article 82 de la loi Informatique et Libertés).",
      ]},
      { h: "2. Cookies tiers", p: [
        "Aucun cookie tiers publicitaire n'est déposé. Aucun outil de mesure d'audience nécessitant un consentement (Google Analytics, Meta Pixel, etc.) n'est intégré dans la version actuelle.",
        "Si un outil d'analyse était ajouté ultérieurement, il ferait l'objet d'un bandeau de consentement conforme aux lignes directrices de la CNIL.",
      ]},
      { h: "3. Gestion", p: [
        "Vous pouvez à tout moment supprimer les cookies via les paramètres de votre navigateur.",
        "La suppression du cookie d'authentification vous déconnecte immédiatement.",
      ]},
    ],
  },
};

const RESUME_TESTIDS = {
  mentions: "legal-mentions",
  rgpd: "legal-rgpd",
  responsabilite: "legal-responsabilite",
  cgu: "legal-cgu",
  cgv: "legal-cgv",
  cookies: "legal-cookies",
};

export default function Legal({ page }) {
  const c = CONTENTS[page] || CONTENTS.mentions;
  const m = META[page] || META.mentions;
  const canonical = `https://voscompagnons.fr${m.path}`;
  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{m.title}</title>
        <meta name="description" content={m.description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:title" content={m.title} />
        <meta property="og:description" content={m.description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        <meta property="og:locale" content="fr_FR" />
      </Helmet>
      <PublicHeader />
      <div className="max-w-3xl mx-auto px-4 py-10">
        <nav className="text-xs text-[#3E4C59] mb-4 flex flex-wrap gap-3">
          <Link to="/mentions-legales" className="underline">Mentions légales</Link>
          <Link to="/confidentialite" className="underline">Confidentialité</Link>
          <Link to="/responsabilite-rgpd" className="underline">Responsabilité RGPD</Link>
          <Link to="/cgu" className="underline">CGU</Link>
          <Link to="/cgv" className="underline">CGV</Link>
          <Link to="/cookies" className="underline">Cookies</Link>
        </nav>
        <h1 className="text-2xl md:text-3xl font-semibold text-[#1F2933]" data-testid={RESUME_TESTIDS[page] || "legal-title"}>{c.title}</h1>
        <p className="mt-2 text-xs text-[#3E4C59]">Dernière mise à jour : février 2026.</p>
        <div className="mt-6 space-y-6 text-sm leading-relaxed text-[#1F2933]">
          {c.sections.map((s, i) => (
            <section key={i}>
              <h2 className="text-base font-semibold text-[#1B3A5C] mt-2">{s.h}</h2>
              <div className="mt-2 space-y-2">
                {s.p.map((line, k) => <p key={k}>{line}</p>)}
              </div>
            </section>
          ))}
        </div>
      </div>
      <PublicFooter />
    </div>
  );
}
